import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import type { CajaFija } from '../componentes/CajaDelEfectivo';
import { useCuentasParaLaCaja, type ConceptoDeCaja } from '../componentes/ConceptosDeCaja';
import { Boton } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { useTextoDeError } from '../componentes/ui/estados';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { DialogoDeCobranza } from './DialogoDeCobranza';
import { DialogoDePago } from './DialogoDePago';

/** Qué es lo que entra o sale del cajón. Es la primera pregunta, y ordena el resto. */
type QueEs = 'gasto' | 'pago' | 'cobro' | 'deposito' | 'dueno';

interface Tercero {
  readonly terceroId: string;
  readonly razonSocial: string;
  readonly saldo: string;
}

interface CuentaBancaria {
  readonly id: string;
  readonly nombre: string;
  readonly banco: string;
}

/** Los conceptos que nacen hechos para el dueño: la pantalla los ofrece como opción propia. */
const DEL_DUENO = { retiro: 'RETIRO_DEL_DUENO', aporte: 'APORTE_DEL_DUENO' } as const;

function esQueEs(valor: string): valor is QueEs {
  return ['gasto', 'pago', 'cobro', 'deposito', 'dueno'].includes(valor);
}

function conDosDecimales(importe: string): string {
  const [entera, fraccion = ''] = importe.split('.');
  return `${entera ?? '0'}.${fraccion.padEnd(2, '0').slice(0, 2)}`;
}

/** Un concepto que hoy se puede usar: activo y con una cuenta que la caja todavía admite. */
function usable(concepto: ConceptoDeCaja): boolean {
  return concepto.activo && concepto.cuenta !== null;
}

/**
 * «Registrar movimiento», que pregunta primero qué es (E37).
 *
 * Hasta acá era un texto libre y un importe, y no dejaba asiento: la cuenta Caja de los libros se
 * alejaba del cajón con cada retiro. Ahora cada opción hace lo que corresponde:
 *
 * - **Un gasto o un ingreso** se clasifica con un concepto —o, quien sabe, con una cuenta— y se
 *   asienta.
 * - **Un pago a un proveedor** y **un cobro a un cliente** son la orden de pago y el recibo de
 *   siempre, con esta caja ya puesta: mueven la cuenta corriente, no sólo el cajón.
 * - **Un depósito** saca el efectivo del cajón y lo pone en el banco.
 * - **El retiro o el aporte del dueño** va al patrimonio, no a resultados.
 */
export function RegistrarMovimiento({
  turnoId,
  caja,
  abierto,
  onCerrar,
}: {
  readonly turnoId: string;
  readonly caja: CajaFija;
  readonly abierto: boolean;
  readonly onCerrar: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeImputar = usePermiso('CONTABILIDAD.ASIENTO.REGISTRAR');
  const puedePagar = usePermiso('CUENTAS.PAGO.REGISTRAR');
  const puedeCobrar = usePermiso('CUENTAS.COBRO.REGISTRAR');
  const puedeVerBancos = usePermiso('BANCOS.CUENTA.VER');

  const [que, setQue] = useState<QueEs>('gasto');
  const [tipo, setTipo] = useState<'egreso' | 'ingreso'>('egreso');
  const [conCuenta, setConCuenta] = useState(false);
  const [conceptoId, setConceptoId] = useState('');
  const [cuentaId, setCuentaId] = useState('');
  const [delDueno, setDelDueno] = useState<'retiro' | 'aporte'>('retiro');
  const [cuentaBancariaId, setCuentaBancariaId] = useState('');
  const [terceroId, setTerceroId] = useState('');
  const [importe, setImporte] = useState('');
  const [detalle, setDetalle] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** El proveedor o el cliente elegido: con él se abre la orden de pago o el recibo. */
  const [siguiendo, setSiguiendo] = useState<Tercero | null>(null);

  const conceptos = useQuery({
    queryKey: ['conceptos-de-caja'],
    enabled: abierto,
    queryFn: () => pedir<ConceptoDeCaja[]>('/conceptos-de-caja'),
  });
  const cuentas = useCuentasParaLaCaja(abierto && puedeImputar);

  const proveedores = useQuery({
    queryKey: ['cuentas-de-proveedores'],
    enabled: abierto && que === 'pago',
    queryFn: () => pedir<Tercero[]>('/cuentas-corrientes?plano=proveedor'),
  });
  const clientes = useQuery({
    queryKey: ['cuentas-corrientes'],
    enabled: abierto && que === 'cobro',
    queryFn: () => pedir<Tercero[]>('/cuentas-corrientes'),
  });
  const bancos = useQuery({
    queryKey: ['cuentas-bancarias'],
    enabled: abierto && que === 'deposito',
    queryFn: () => pedir<CuentaBancaria[]>('/cuentas-bancarias'),
  });

  const paraElGasto = (conceptos.data ?? []).filter(
    (concepto) =>
      usable(concepto) &&
      (concepto.sentido === 'ambos' || concepto.sentido === tipo) &&
      concepto.codigo !== DEL_DUENO.retiro &&
      concepto.codigo !== DEL_DUENO.aporte,
  );
  const conceptoDelDueno = (conceptos.data ?? []).find(
    (concepto) => concepto.codigo === DEL_DUENO[delDueno] && usable(concepto),
  );
  const terceros = que === 'pago' ? (proveedores.data ?? []) : (clientes.data ?? []);

  function limpiar(): void {
    setConceptoId('');
    setCuentaId('');
    setTerceroId('');
    setCuentaBancariaId('');
    setImporte('');
    setDetalle('');
    setError(null);
  }

  async function refrescar(): Promise<void> {
    await Promise.all([
      clienteDeConsultas.invalidateQueries({ queryKey: ['movimientos-de-caja'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['arqueo'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['cuentas-bancarias'] }),
    ]);
  }

  const registrar = useMutation({
    mutationFn: () => {
      const monto = importe.trim().replace(',', '.');
      if (que === 'deposito') {
        return pedir<unknown>(`/turnos-de-caja/${turnoId}/depositos`, {
          method: 'POST',
          cuerpo: { cuentaBancariaId, importe: monto },
        });
      }
      const clasificacion =
        que === 'dueno'
          ? { conceptoDeCajaId: conceptoDelDueno?.id ?? '' }
          : conCuenta
            ? { cuentaContableId: cuentaId }
            : { conceptoDeCajaId: conceptoId };
      return pedir<unknown>(`/turnos-de-caja/${turnoId}/movimientos`, {
        method: 'POST',
        cuerpo: {
          tipo: que === 'dueno' ? (delDueno === 'retiro' ? 'egreso' : 'ingreso') : tipo,
          importe: monto,
          ...clasificacion,
          ...(detalle.trim() === '' ? {} : { detalle: detalle.trim() }),
        },
      });
    },
    onSuccess: async () => {
      limpiar();
      onCerrar();
      await refrescar();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const faltaClasificar =
    que === 'gasto'
      ? conCuenta
        ? cuentaId === ''
        : conceptoId === ''
      : que === 'dueno'
        ? conceptoDelDueno === undefined
        : que === 'deposito'
          ? cuentaBancariaId === ''
          : false;

  const deTercero = que === 'pago' || que === 'cobro';

  return (
    <>
      <Dialogo
        abierto={abierto && siguiendo === null}
        titulo={t('caja.movimiento')}
        onCerrar={() => {
          limpiar();
          onCerrar();
        }}
        acciones={
          deTercero ? (
            <Boton
              tono="principal"
              disabled={terceroId === ''}
              onClick={() => {
                setSiguiendo(terceros.find((tercero) => tercero.terceroId === terceroId) ?? null);
              }}
            >
              {t('caja.seguir')}
            </Boton>
          ) : (
            <Boton
              tono="principal"
              cargando={registrar.isPending}
              disabled={importe.trim() === '' || faltaClasificar}
              onClick={() => {
                registrar.mutate();
              }}
            >
              {t('acciones.guardar')}
            </Boton>
          )
        }
      >
        <div className="flex flex-col gap-3">
          <Selector
            etiqueta={t('caja.queEs')}
            value={que}
            onChange={(evento) => {
              if (esQueEs(evento.target.value)) setQue(evento.target.value);
              limpiar();
            }}
          >
            <option value="gasto">{t('caja.queGasto')}</option>
            {puedePagar ? <option value="pago">{t('caja.quePago')}</option> : null}
            {puedeCobrar ? <option value="cobro">{t('caja.queCobro')}</option> : null}
            {puedeVerBancos ? <option value="deposito">{t('caja.queDeposito')}</option> : null}
            <option value="dueno">{t('caja.queDueno')}</option>
          </Selector>

          {que !== 'gasto' ? null : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Selector
                  etiqueta={t('caja.tipo')}
                  value={tipo}
                  onChange={(evento) => {
                    setTipo(evento.target.value === 'ingreso' ? 'ingreso' : 'egreso');
                    setConceptoId('');
                  }}
                >
                  <option value="egreso">{t('caja.egreso')}</option>
                  <option value="ingreso">{t('caja.ingreso')}</option>
                </Selector>
                {/* Imputar directo es decidir un asiento: sin ese permiso no se ofrece. */}
                {puedeImputar ? (
                  <Selector
                    etiqueta={t('caja.clasificarCon')}
                    value={conCuenta ? 'cuenta' : 'concepto'}
                    onChange={(evento) => {
                      setConCuenta(evento.target.value === 'cuenta');
                    }}
                  >
                    <option value="concepto">{t('caja.conConcepto')}</option>
                    <option value="cuenta">{t('caja.conCuenta')}</option>
                  </Selector>
                ) : null}
              </div>

              {conCuenta ? (
                <Selector
                  etiqueta={t('caja.cuentaDelPlan')}
                  obligatorio
                  ayuda={t('caja.ayudaCuenta')}
                  value={cuentaId}
                  onChange={(evento) => {
                    setCuentaId(evento.target.value);
                  }}
                >
                  <option value="">{t('caja.elegirCuenta')}</option>
                  {cuentas.map((cuenta) => (
                    <option key={cuenta.id} value={cuenta.id}>
                      {cuenta.codigo} {cuenta.nombre}
                    </option>
                  ))}
                </Selector>
              ) : (
                <Selector
                  etiqueta={t('caja.conceptoDeCaja')}
                  obligatorio
                  value={conceptoId}
                  onChange={(evento) => {
                    setConceptoId(evento.target.value);
                  }}
                >
                  <option value="">{t('caja.elegirConcepto')}</option>
                  {paraElGasto.map((concepto) => (
                    <option key={concepto.id} value={concepto.id}>
                      {concepto.nombre}
                    </option>
                  ))}
                </Selector>
              )}
            </>
          )}

          {que !== 'dueno' ? null : (
            <>
              <Selector
                etiqueta={t('caja.tipo')}
                value={delDueno}
                ayuda={t('caja.ayudaDueno')}
                onChange={(evento) => {
                  setDelDueno(evento.target.value === 'aporte' ? 'aporte' : 'retiro');
                }}
              >
                <option value="retiro">{t('caja.retiro')}</option>
                <option value="aporte">{t('caja.aporte')}</option>
              </Selector>
              {conceptos.isSuccess && conceptoDelDueno === undefined ? (
                <p role="status" className="text-xs text-atencion">
                  {t('caja.sinConceptoDelDueno')}
                </p>
              ) : null}
            </>
          )}

          {que !== 'deposito' ? null : (
            <Selector
              etiqueta={t('caja.cuentaBancaria')}
              obligatorio
              ayuda={t('caja.ayudaDeposito')}
              value={cuentaBancariaId}
              onChange={(evento) => {
                setCuentaBancariaId(evento.target.value);
              }}
            >
              <option value="">
                {bancos.isSuccess && bancos.data.length === 0
                  ? t('caja.sinCuentasBancarias')
                  : t('caja.elegirCuentaBancaria')}
              </option>
              {(bancos.data ?? []).map((cuenta) => (
                <option key={cuenta.id} value={cuenta.id}>
                  {cuenta.nombre} · {cuenta.banco}
                </option>
              ))}
            </Selector>
          )}

          {!deTercero ? null : (
            <Selector
              etiqueta={que === 'pago' ? t('caja.proveedor') : t('caja.cliente')}
              obligatorio
              ayuda={t('caja.ayudaTercero', { puesto: caja.nombre })}
              value={terceroId}
              onChange={(evento) => {
                setTerceroId(evento.target.value);
              }}
            >
              <option value="">
                {terceros.length === 0
                  ? que === 'pago'
                    ? t('caja.sinProveedores')
                    : t('caja.sinClientes')
                  : t('caja.elegirTercero')}
              </option>
              {terceros.map((tercero) => (
                <option key={tercero.terceroId} value={tercero.terceroId}>
                  {tercero.razonSocial} · {conDosDecimales(tercero.saldo)}
                </option>
              ))}
            </Selector>
          )}

          {deTercero ? null : (
            <>
              <Campo
                etiqueta={t('caja.importe')}
                obligatorio
                inputMode="decimal"
                value={importe}
                ayuda={que === 'gasto' && tipo === 'egreso' ? t('caja.avisoIva') : undefined}
                onChange={(evento) => {
                  setImporte(evento.target.value);
                }}
              />
              {que === 'deposito' ? null : (
                <Campo
                  etiqueta={t('caja.detalle')}
                  maxLength={200}
                  value={detalle}
                  ayuda={t('caja.ayudaDetalle')}
                  onChange={(evento) => {
                    setDetalle(evento.target.value);
                  }}
                />
              )}
            </>
          )}

          {error === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>

      {/* El pago y el cobro son la orden y el recibo de siempre, con esta caja ya puesta. */}
      {que === 'pago' ? (
        <DialogoDePago
          proveedor={siguiendo}
          cajaFija={caja}
          onCerrar={() => {
            setSiguiendo(null);
            limpiar();
            onCerrar();
          }}
        />
      ) : null}
      {que === 'cobro' ? (
        <DialogoDeCobranza
          terceroId={siguiendo?.terceroId ?? null}
          nombre={siguiendo?.razonSocial ?? ''}
          abierto={siguiendo !== null}
          cajaFija={caja}
          onCerrar={() => {
            setSiguiendo(null);
            limpiar();
            onCerrar();
          }}
        />
      ) : null}
    </>
  );
}
